import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  Overlay,
  Popover,
} from 'react-bootstrap';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { format } from 'date-fns';

import PageWrapper from 'components/PageWrapper';
import CommonLoader from 'components/CommonLoader';
import EnhetTypeahead from 'components/EnhetTypeahead';
import GrupperingPicker from 'components/GrupperingPicker';

import SalgFraTilknyttetGrossistGruppeApi from 'services/KjederegisteretAdminApi/SalgFraTilknyttetGrossistGruppeApi';
import {
  SalgFraTilknyttetGrossistGruppeDto,
  SalgFraTilknyttedeGrossistGrupperDto,
} from 'services/KjederegisteretAdminApi/SalgFraTilknyttetGrossistGruppeApi/types';
import { PickedEnhet } from 'components/EnhetTypeahead/types';
import { IEnhetsgruppeNode } from 'components/GrupperingPicker/types';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import { EnhetViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import HttpErrorHelper from 'utils/HttpErrorHelper';

interface FilterState {
  enhetId: number | null;
  enhetsGruppeId: number | null;
  grossistGruppe: string | null;
  showIgnorertOnly: boolean;
  showSameGrossistTilknyttetManyEnhetsgrupperForEnhet: boolean;
}

const GROSSIST_GRUPPER = [
  { value: '', label: 'Alle grossistgrupper' },
  { value: 'NG', label: 'NG' },
  { value: 'SERVISGROS', label: 'SERVISGROS' },
  { value: 'DLVRY', label: 'DLVRY' },
];

const ITEMS_PER_PAGE = 50;

const SalgFraTilknyttetGrossistgruppe = () => {
  const isMountedRef = useIsMountedRef();
  const toaster = useToaster();

  const [data, setData] = useState<SalgFraTilknyttetGrossistGruppeDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState<boolean>(false);
  const [selectedEnhet, setSelectedEnhet] = useState<EnhetViewModel | null>(null);
  const [isLoadingEnhet, setIsLoadingEnhet] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterState>({
    enhetId: null,
    enhetsGruppeId: null,
    grossistGruppe: null,
    showIgnorertOnly: false,
    showSameGrossistTilknyttetManyEnhetsgrupperForEnhet: false,
  });

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuTargets = useRef<Map<number, HTMLElement>>(new Map());
  const [updatingLopenr, setUpdatingLopenr] = useState<number | null>(null);

  const handleOppdaterSalgsdata = async (lopenr: number) => {
    setOpenMenuId(null);
    try {
      await SalgFraTilknyttetGrossistGruppeApi.oppdaterSistFakturertDato({
        enhetsgruppeNr: [],
        lopenummer: [lopenr],
      });
      toaster.success('Oppdatering startet', 'Salgsdata er lagt i kø for oppdatering.');
      setUpdatingLopenr(lopenr);
      setTimeout(() => {
        setUpdatingLopenr(null);
        setCurrentPage(1);
        loadDataInternal(1, true);
      }, 4000);
    } catch (error) {
      toaster.error('Feil ved oppdatering av salgsdata', HttpErrorHelper.formatError(error));
    }
  };

  const loadDataInternal = async (page: number, isNewSearch: boolean) => {
    if (!isMountedRef.current) return;

    console.log('📊 loadDataInternal called:', { page, isNewSearch });
    setIsLoading(true);
    try {
      const skip = (page - 1) * ITEMS_PER_PAGE;
      const params = {
        take: ITEMS_PER_PAGE,
        skip,
        enhetId: filters.enhetId,
        enhetsGruppeId: filters.enhetsGruppeId,
        grossistGruppe: filters.grossistGruppe,
        showIgnorertOnly: filters.showIgnorertOnly,
        showSameGrossistTilknyttetManyEnhetsgrupperForEnhet:
          filters.showSameGrossistTilknyttetManyEnhetsgrupperForEnhet,
      };

      console.log('🌐 API call with params:', params);

      const response =
        await SalgFraTilknyttetGrossistGruppeApi.getSalgFraTilknyttetGrossistGruppe(params);

      if (isMountedRef.current) {
        const newItems = response.items || [];
        console.log(
          '📦 Received items:',
          newItems.length,
          'Setting hasMoreData to:',
          newItems.length === ITEMS_PER_PAGE,
        );
        setHasMoreData(newItems.length === ITEMS_PER_PAGE);

        if (isNewSearch) {
          console.log('🔄 New search - replacing data');
          setData(newItems);
        } else {
          console.log('➕ Appending to existing data');
          setData((prevData) => {
            const newData = [...prevData, ...newItems];
            console.log(
              '📈 Data updated - previous:',
              prevData.length,
              'new total:',
              newData.length,
            );
            return newData;
          });
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('❌ Error loading data:', error);
        if (isNewSearch) {
          setData([]);
        }
        setHasMoreData(false);
        toaster.error('Feil ved lasting av data', HttpErrorHelper.formatError(error));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const loadTotalCount = async () => {
    if (!isMountedRef.current) return;

    setIsLoadingCount(true);
    try {
      const countParams = {
        enhetId: filters.enhetId,
        enhetsGruppeId: filters.enhetsGruppeId,
        grossistGruppe: filters.grossistGruppe,
        showIgnorertOnly: filters.showIgnorertOnly,
        showSameGrossistTilknyttetManyEnhetsgrupperForEnhet:
          filters.showSameGrossistTilknyttetManyEnhetsgrupperForEnhet,
      };

      const count =
        await SalgFraTilknyttetGrossistGruppeApi.getSalgFraTilknyttetGrossistGruppeCount(
          countParams,
        );

      if (isMountedRef.current) {
        setTotalCount(count);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('❌ Error loading count:', error);
        setTotalCount(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingCount(false);
      }
    }
  };

  const loadMoreData = async () => {
    console.log('🔄 loadMoreData called:', { hasMoreData, isLoading, currentPage });

    if (!hasMoreData || isLoading) {
      console.log('⚠️ Skipping load - hasMoreData:', hasMoreData, 'isLoading:', isLoading);
      return;
    }

    const nextPage = currentPage + 1;
    console.log('📄 Loading page:', nextPage);
    setCurrentPage(nextPage);
    await loadDataInternal(nextPage, false);
  };

  const { isFetching, sentinelRef } = useInfiniteScroll(loadMoreData);

  const handleToggleIgnorer = async (id: number) => {
    try {
      await SalgFraTilknyttetGrossistGruppeApi.ToogleIgnorer(id);
      // Reload the first page and reset data
      setCurrentPage(1);
      await loadDataInternal(1, true);
      await loadTotalCount(); // Also reload the count
      toaster.success('Suksess!', 'Ignorert status oppdatert');
    } catch (error) {
      console.error('Error toggling ignorert:', error);
      toaster.error('Feil ved oppdatering av ignorert status', HttpErrorHelper.formatError(error));
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    loadDataInternal(1, true);
    loadTotalCount();
  }, [filters]); // Reset to first page when filters change

  // Fetch enhet details when enhetId changes
  useEffect(() => {
    const fetchEnhetDetails = async () => {
      if (!filters.enhetId || !isMountedRef.current) {
        setSelectedEnhet(null);
        return;
      }

      setIsLoadingEnhet(true);
      try {
        const enhet = await EnhetApi.getEnhetById(filters.enhetId);
        if (isMountedRef.current) {
          setSelectedEnhet(enhet);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Error loading enhet details:', error);
          setSelectedEnhet(null);
          toaster.error('Feil ved lasting av enhet detaljer', HttpErrorHelper.formatError(error));
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingEnhet(false);
        }
      }
    };

    fetchEnhetDetails();
  }, [filters.enhetId]);

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
    setHasMoreData(true);
  };

  const handleEnhetSelect = (enhet: PickedEnhet) => {
    handleFilterChange({ enhetId: enhet.id });
  };

  const handleEnhetsgruppeSelect = (grupperinger: IEnhetsgruppeNode[]) => {
    const selectedId = grupperinger.length > 0 ? grupperinger[0].id : null;
    handleFilterChange({ enhetsGruppeId: selectedId });
  };

  const handleResetEnhet = () => {
    handleFilterChange({ enhetId: null });
  };

  const clearFilters = () => {
    setFilters({
      enhetId: null,
      enhetsGruppeId: null,
      grossistGruppe: null,
      showIgnorertOnly: false,
      showSameGrossistTilknyttetManyEnhetsgrupperForEnhet: false,
    });
    setCurrentPage(1);
    setHasMoreData(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Ikke fakturert';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy');
    } catch {
      return 'Ugyldig dato';
    }
  };

  const totalItems = data.length;

  return (
    <PageWrapper isFluid header="Innkjøp og grossister">
      {/* Loading indicator */}
      {(isLoading || isLoadingCount) && (
        <Alert variant="light" className="mb-3 d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>
            {isLoading && isLoadingCount
              ? 'Laster data og teller rader...'
              : isLoading
                ? 'Laster data...'
                : 'Teller rader...'}
          </span>
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Filtre</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} lg={4}>
              <Form.Group className="mb-3">
                <Form.Label>Enhet</Form.Label>
                <EnhetTypeahead
                  onChange={handleEnhetSelect}
                  selectedLopenummer={filters.enhetId?.toString()}
                />
                {selectedEnhet && (
                  <div className="mt-2 mb-2 p-2 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{selectedEnhet.enhetnavn}</strong>
                        <br />
                        <small className="text-muted">
                          Løpenr: {selectedEnhet.lopenr} | Org.nr:{' '}
                          {selectedEnhet.firma?.organisasjonsnr || 'N/A'}
                        </small>
                        {selectedEnhet.firma?.nedlagtDato && (
                          <Badge bg="danger" className="ms-2">
                            Nedlagt
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleResetEnhet}
                        title="Fjern valgt enhet"
                      >
                        X
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6} lg={4}>
              <Form.Group className="mb-3">
                <Form.Label>Innkjøpssamarbeid</Form.Label>
                <GrupperingPicker
                  onSelectionChanged={handleEnhetsgruppeSelect}
                  closeOnSelect={true}
                  treeSelectMode="radioSelect"
                  onlyTopLevel={true}
                  enhetgrupperingType="IS"
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={4}>
              <Form.Group className="mb-3">
                <Form.Label>Grossistgruppe</Form.Label>
                <Form.Select
                  value={filters.grossistGruppe || ''}
                  onChange={(e) =>
                    handleFilterChange({
                      grossistGruppe: e.target.value || null,
                    })
                  }
                >
                  {GROSSIST_GRUPPER.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="showIgnorertOnly"
                label="Vis kun ignorerte"
                checked={filters.showIgnorertOnly}
                onChange={(e) => handleFilterChange({ showIgnorertOnly: e.target.checked })}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="showSameGrossist"
                label="Vis samme grossist tilknyttet mange enhetsgrupper for enhet"
                checked={filters.showSameGrossistTilknyttetManyEnhetsgrupperForEnhet}
                onChange={(e) =>
                  handleFilterChange({
                    showSameGrossistTilknyttetManyEnhetsgrupperForEnhet: e.target.checked,
                  })
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Button variant="secondary" onClick={clearFilters} className="mt-2">
                Nullstill filtre
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Results */}
      {isLoading && data.length === 0 ? (
        <CommonLoader />
      ) : (
        <>
          {data.length > 0 ? (
            <>
              <Alert variant="info" className="mb-3">
                {totalCount !== null ? (
                  <>
                    Viser {data.length} av {totalCount} rader
                  </>
                ) : isLoadingCount ? (
                  <>Viser {data.length} rader (teller totalt...)</>
                ) : (
                  <>Viser {data.length} rader</>
                )}
              </Alert>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Løpenr</th>
                    <th>Enhet</th>
                    <th>Innkjøpssamarbeid</th>
                    <th>Grossistgruppe</th>
                    <th>Sist fakturert</th>
                    <th>Ignorert</th>
                    <th>Handlinger</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item: SalgFraTilknyttetGrossistGruppeDto) => (
                    <tr key={item.salgFraTilknyttetGrossistGruppeID}>
                      <td>{item.lopenr || 'Ikke satt'}</td>
                      <td>
                        <a
                          href={`/enheter/${item.enhetID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.enhetnavn}
                        </a>
                      </td>
                      <td>{item.enhetsgrupperingnavn}</td>
                      <td>{item.grossistGruppe}</td>
                      <td>{formatDate(item.sistFakturertDato)}</td>
                      <td>
                        {item.ignorer ? (
                          <span className="badge bg-danger">Ja</span>
                        ) : (
                          <span className="badge bg-success">Nei</span>
                        )}
                      </td>
                      <td className="d-flex align-items-center gap-2">
                        <Button
                          variant={item.ignorer ? 'outline-success' : 'outline-danger'}
                          size="sm"
                          onClick={() =>
                            handleToggleIgnorer(item.salgFraTilknyttetGrossistGruppeID)
                          }
                          title={item.ignorer ? 'Fjern ignorering' : 'Ignorer'}
                        >
                          {item.ignorer ? 'Aktiver' : 'Ignorer'}
                        </Button>
                        {updatingLopenr === item.lopenr && (
                          <Spinner animation="border" size="sm" title="Oppdaterer salgsdata..." />
                        )}
                        <span
                          ref={(el) => {
                            if (el)
                              menuTargets.current.set(item.salgFraTilknyttetGrossistGruppeID, el);
                          }}
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            setOpenMenuId(
                              openMenuId === item.salgFraTilknyttetGrossistGruppeID
                                ? null
                                : item.salgFraTilknyttetGrossistGruppeID,
                            )
                          }
                        >
                          <IcebergIcon icon="ellipsis-horizontal" size="20px" />
                        </span>
                        <Overlay
                          show={openMenuId === item.salgFraTilknyttetGrossistGruppeID}
                          target={
                            menuTargets.current.get(item.salgFraTilknyttetGrossistGruppeID) ?? null
                          }
                          placement="bottom"
                          rootClose
                          onHide={() => setOpenMenuId(null)}
                        >
                          <Popover id={`menu-${item.salgFraTilknyttetGrossistGruppeID}`}>
                            <Popover.Body className="p-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                style={{ border: 0, whiteSpace: 'nowrap' }}
                                onClick={() =>
                                  item.lopenr !== null && handleOppdaterSalgsdata(item.lopenr)
                                }
                              >
                                Oppdater salgsdata
                              </Button>
                            </Popover.Body>
                          </Popover>
                        </Overlay>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Infinite scroll sentinel and loading indicator */}
              {hasMoreData && (
                <div
                  ref={sentinelRef}
                  className="d-flex justify-content-center p-4 bg-light border"
                  style={{ minHeight: '80px' }}
                >
                  {isFetching || isLoading ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Laster flere resultater...</span>
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center">
                      <div className="mb-2">Scroll ned for å laste flere resultater</div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          console.log('🔘 Manual button clicked with state:', {
                            hasMoreData,
                            isLoading,
                            currentPage,
                            dataLength: data.length,
                            totalCount,
                          });
                          loadMoreData();
                        }}
                        disabled={!hasMoreData || isLoading}
                      >
                        Last flere manuelt
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!hasMoreData && data.length > ITEMS_PER_PAGE && (
                <div className="d-flex justify-content-center p-3">
                  <small className="text-muted">Alle resultater er lastet</small>
                </div>
              )}
            </>
          ) : (
            <Alert variant="info">Ingen resultater funnet med de valgte filtrene.</Alert>
          )}
        </>
      )}
    </PageWrapper>
  );
};

export default SalgFraTilknyttetGrossistgruppe;
